import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
const lipstickImg = "/lipstick-hero.webp";
import eyelinerImg from "@/assets/eyeliner-hero.webp";
import compactImg from "@/assets/compact-hero.webp";

const Hero = () => {
  const prefersReducedMotion = useReducedMotion();
  const float = (anim: any) => (prefersReducedMotion ? undefined : anim);
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-background pt-20">
      {/* Background Glow Effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[120px] animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-accent/10 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: "1s" }} />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="text-center lg:text-left"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/50 border border-border mb-6"
            >
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-sm font-subtitle neon-pink-glow">New Collection Available</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-semibold leading-tight mb-6"
            >
              Enhance Your
              <br />
              <span className="text-primary glow-text">Natural Allure</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="font-subtitle text-base sm:text-lg text-muted-foreground max-w-lg mb-8 mx-auto lg:mx-0"
            >
              Luxury beauty made effortless. Discover premium cosmetics crafted for the modern woman.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
            >
              <Button variant="hero" size="xl" className="group w-full sm:w-auto" asChild>
                <Link to="/shop">
                  Shop Collection
                  <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </motion.div>
          </motion.div>

          {/* Floating Products */}
          <div className="relative h-[360px] sm:h-[500px] lg:h-[600px]">
            {/* Center Lipstick */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20"
            >
              <motion.img
                src={lipstickImg}
                alt="Luxury Lipstick"
                width={256}
                height={341}
                loading="eager"
                fetchPriority="high"
                decoding="async"
                className="w-40 sm:w-48 md:w-64 h-auto drop-shadow-[0_0_30px_hsl(350,100%,88%,0.3)]"
                animate={float({ y: [-10, 10, -10] })}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              />
            </motion.div>

            {/* Eyeliner - Left */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
              className="absolute left-0 top-1/4 z-10"
            >
              <motion.img
                src={eyelinerImg}
                alt="Luxury Eyeliner"
                width={176}
                height={235}
                loading="eager"
                fetchPriority="high"
                decoding="async"
                className="w-24 sm:w-32 md:w-44 h-auto drop-shadow-[0_0_20px_hsl(350,100%,88%,0.2)]"
                animate={float({ y: [5, -15, 5], rotate: [-2, 2, -2] })}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
              />
            </motion.div>

            {/* Compact - Right */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.8, duration: 0.8 }}
              className="absolute right-0 bottom-1/4 z-10"
            >
              <motion.img
                src={compactImg}
                alt="Luxury Compact"
                width={208}
                height={277}
                loading="eager"
                decoding="async"
                className="w-28 sm:w-36 md:w-52 h-auto drop-shadow-[0_0_25px_hsl(350,100%,88%,0.25)]"
                animate={float({ y: [-5, 15, -5], rotate: [1, -1, 1] })}
                transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              />
            </motion.div>

          </div>
        </div>
      </div>

      {/* Bottom Gradient Fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent" />
    </section>
  );
};

export default Hero;